import React, { useState, useEffect } from 'react';
import { ArrowUp, ArrowDown, Lock, FileText, FileSpreadsheet } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';

// T-135: ícono de tipo de hoja (texto vs cálculo). Réplica del helper de SheetTabs.
const SheetTypeIcon = ({ sheet, className = 'w-3.5 h-3.5 shrink-0 text-slate-400 dark:text-slate-500' }) =>
  sheet.content_type === 'spreadsheet'
    ? <FileSpreadsheet className={className} title="Hoja de cálculo" />
    : <FileText className={className} title="Hoja de texto" />;

// T-128: diálogo de reordenar hojas con flechas ↑↓ (móvil/tablet donde el
// <select> nativo solo permite elegir). Persiste via onReorder (= reorderSheets
// del padre: PUT por hoja + setSheets + toast). No toca el backend directo.
const SheetReorderDialog = ({ open, onOpenChange, sheets, groups = [], onReorder }) => {
  const [order, setOrder] = useState([]);

  // Reinicializa la copia local ordenada cada vez que se abre el diálogo.
  useEffect(() => {
    if (open) {
      setOrder([...sheets].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
    }
  }, [open, sheets]);

  // Swap adyacente: dir = -1 sube, +1 baja.
  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= order.length) return;
    const next = [...order];
    [next[idx], next[j]] = [next[j], next[idx]];
    setOrder(next);
  };

  // Reindexa 0..N-1 y delega la persistencia al padre. Un solo PUT-batch al guardar.
  const save = () => {
    const reindexed = order.map((s, i) => ({ ...s, order: i }));
    onReorder(reindexed);
    onOpenChange(false);
  };

  const groupColor = (sheet) => {
    const g = groups.find((g2) => g2.id === sheet.group_id);
    return g?.color || null;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass border-0 max-w-md max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Ordenar hojas</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-1">
          {order.map((sheet, idx) => {
            const color = groupColor(sheet);
            return (
              <div
                key={sheet.id}
                className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-white/40 dark:bg-slate-800/40"
              >
                {color && (
                  <span
                    style={{ backgroundColor: color }}
                    className="w-2 h-2 rounded-full shrink-0"
                  />
                )}
                <SheetTypeIcon sheet={sheet} />
                {sheet.is_source && (
                  <Lock className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                )}
                <span className="flex-1 min-w-0 truncate text-sm dark:text-slate-100">
                  {sheet.name}
                </span>
                <div className="flex items-center gap-0.5 shrink-0">
                  <button
                    type="button"
                    onClick={() => move(idx, -1)}
                    disabled={idx === 0}
                    title="Subir"
                    className="p-1 rounded hover:bg-white/50 dark:hover:bg-slate-700/50 disabled:opacity-40 disabled:pointer-events-none"
                  >
                    <ArrowUp className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => move(idx, 1)}
                    disabled={idx === order.length - 1}
                    title="Bajar"
                    className="p-1 rounded hover:bg-white/50 dark:hover:bg-slate-700/50 disabled:opacity-40 disabled:pointer-events-none"
                  >
                    <ArrowDown className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={save}>Guardar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SheetReorderDialog;
